/**
 * PWA Install Prompt Component
 * Intelligent, persistent install prompts for Android, iOS, and Desktop
 */

import React, { useState, useEffect, memo } from 'react';
import { usePWA } from '../context/PWAContext';

// ==========================================
// MAIN INSTALL PROMPT COMPONENT
// ==========================================

const PWAInstallPrompt = memo(() => {
  const {
    isInstallable,
    isInstalled,
    showInstallPrompt,
    promptInstall,
    dismissInstallPrompt,
    shouldShowIOSInstallGuide,
    platform
  } = usePWA();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Don't render if already installed
  if (isInstalled) return null;

  // Show iOS-specific guide
  if (shouldShowIOSInstallGuide) {
    return <IOSInstallGuide onDismiss={dismissInstallPrompt} />;
  }

  // Show Android/Desktop install prompt
  if (!showInstallPrompt || !isInstallable) return null;

  const handleInstall = async () => {
    setIsAnimating(true);
    const result = await promptInstall();
    setIsAnimating(false);

    if (result.outcome === 'accepted') {
      // Success animation would play here
    }
  };

  return (
    <>
      {/* Backdrop - soft dismiss (shows again on next visit) */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998] transition-opacity duration-300"
        onClick={() => dismissInstallPrompt(null)}
      />

      {/* Install Card */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 pb-safe animate-slide-up">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-[#7070F5] to-[#8B5CF6] p-6 text-white">
            <div className="flex items-start gap-4">
              {/* App Icon */}
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold mb-1">Install GreenFlow</h3>
                <p className="text-white/80 text-sm">
                  Get the full app experience with faster loading and offline access
                </p>
              </div>

              {/* Close button - soft dismiss (shows again on next visit) */}
              <button
                onClick={() => dismissInstallPrompt(null)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Benefits */}
          <div className="p-6">
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Benefit
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                }
                label="Instant Load"
              />
              <Benefit
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
                  </svg>
                }
                label="Works Offline"
              />
              <Benefit
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                }
                label="Notifications"
              />
            </div>

            {/* Action buttons */}
            <div className="space-y-3">
              {/* Main action row */}
              <div className="flex gap-3">
                <button
                  onClick={() => dismissInstallPrompt('remind')}
                  className="flex-1 py-3 px-4 text-gray-600 font-medium rounded-xl hover:bg-gray-100 transition-colors border border-gray-200"
                >
                  Remind Later
                </button>
                <button
                  onClick={handleInstall}
                  disabled={isAnimating}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-[#7070F5] to-[#8B5CF6] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/30 transition-all disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {isAnimating ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  )}
                  Install App
                </button>
              </div>
              {/* Secondary dismiss option */}
              <button
                onClick={() => dismissInstallPrompt('permanent')}
                className="w-full py-2 text-gray-400 text-sm hover:text-gray-600 transition-colors"
              >
                Don't ask again
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .pb-safe {
          padding-bottom: max(1rem, env(safe-area-inset-bottom));
        }
      `}</style>
    </>
  );
});

// ==========================================
// BENEFIT ITEM COMPONENT
// ==========================================

const Benefit = memo(({ icon, label }) => (
  <div className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-xl">
    <div className="text-[#7070F5]">{icon}</div>
    <span className="text-xs font-medium text-gray-600">{label}</span>
  </div>
));

// ==========================================
// iOS INSTALL GUIDE COMPONENT
// ==========================================

const IOSInstallGuide = memo(({ onDismiss }) => {
  const [step, setStep] = useState(1);

  return (
    <>
      {/* Backdrop - soft dismiss (shows again on next visit) */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998]"
        onClick={() => onDismiss(null)}
      />

      {/* Guide Card */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 pb-safe animate-slide-up">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#7070F5] to-[#8B5CF6] p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Install GreenFlow</h3>
              <button
                onClick={() => onDismiss(null)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-white/80 text-sm">
              Add GreenFlow to your home screen for the best experience
            </p>
          </div>

          {/* Steps */}
          <div className="p-6">
            <div className="space-y-4">
              {/* Step 1 */}
              <div className={`flex items-start gap-4 p-4 rounded-xl transition-all ${step === 1 ? 'bg-purple-50 border-2 border-[#7070F5]' : 'bg-gray-50'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${step === 1 ? 'bg-[#7070F5] text-white' : 'bg-gray-200 text-gray-500'}`}>
                  1
                </div>
                <div>
                  <p className="font-medium text-gray-900 mb-1">
                    Tap the Share button
                  </p>
                  <p className="text-sm text-gray-500">
                    Look for the share icon at the bottom of Safari
                  </p>
                  <div className="mt-2 flex items-center gap-2 text-[#7070F5]">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    <span className="text-sm font-medium">Share</span>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className={`flex items-start gap-4 p-4 rounded-xl transition-all ${step === 2 ? 'bg-purple-50 border-2 border-[#7070F5]' : 'bg-gray-50'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${step === 2 ? 'bg-[#7070F5] text-white' : 'bg-gray-200 text-gray-500'}`}>
                  2
                </div>
                <div>
                  <p className="font-medium text-gray-900 mb-1">
                    Scroll and tap "Add to Home Screen"
                  </p>
                  <p className="text-sm text-gray-500">
                    Find it in the share menu options
                  </p>
                  <div className="mt-2 flex items-center gap-2 text-[#7070F5]">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-sm font-medium">Add to Home Screen</span>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className={`flex items-start gap-4 p-4 rounded-xl transition-all ${step === 3 ? 'bg-purple-50 border-2 border-[#7070F5]' : 'bg-gray-50'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${step === 3 ? 'bg-[#7070F5] text-white' : 'bg-gray-200 text-gray-500'}`}>
                  3
                </div>
                <div>
                  <p className="font-medium text-gray-900 mb-1">
                    Tap "Add" to confirm
                  </p>
                  <p className="text-sm text-gray-500">
                    GreenFlow will appear on your home screen
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="space-y-3 mt-6">
              <div className="flex gap-3">
                <button
                  onClick={() => onDismiss('remind')}
                  className="flex-1 py-3 px-4 text-gray-600 font-medium rounded-xl hover:bg-gray-100 transition-colors border border-gray-200"
                >
                  Remind Later
                </button>
                <button
                  onClick={() => {
                    if (step < 3) {
                      setStep(prev => prev + 1);
                    } else {
                      onDismiss('remind');
                    }
                  }}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-[#7070F5] to-[#8B5CF6] text-white font-semibold rounded-xl hover:shadow-lg transition-all"
                >
                  {step < 3 ? 'Next Step' : 'Got It'}
                </button>
              </div>
              <button
                onClick={() => onDismiss('permanent')}
                className="w-full py-2 text-gray-400 text-sm hover:text-gray-600 transition-colors"
              >
                Don't ask again
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .pb-safe {
          padding-bottom: max(1rem, env(safe-area-inset-bottom));
        }
      `}</style>
    </>
  );
});

// ==========================================
// UPDATE AVAILABLE PROMPT
// ==========================================

export const PWAUpdatePrompt = memo(() => {
  const { showUpdatePrompt, applyUpdate, dismissUpdatePrompt } = usePWA();

  if (!showUpdatePrompt) return null;

  return (
    <div className="fixed top-4 left-4 right-4 z-[9999] animate-slide-down">
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="p-4 flex items-center gap-4">
          {/* Icon */}
          <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900">Update Available</h4>
            <p className="text-sm text-gray-500">A new version of GreenFlow is ready</p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={dismissUpdatePrompt}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <button
              onClick={applyUpdate}
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all"
            >
              Update
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-down {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-down {
          animation: slide-down 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
});

// ==========================================
// OFFLINE INDICATOR
// ==========================================

export const OfflineIndicator = memo(() => {
  const { isOnline } = usePWA();

  if (isOnline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium safe-top">
      <div className="flex items-center justify-center gap-2">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
        </svg>
        <span>You're offline. Some features may be unavailable.</span>
      </div>

      <style jsx>{`
        .safe-top {
          padding-top: max(0.5rem, env(safe-area-inset-top));
        }
      `}</style>
    </div>
  );
});

// ==========================================
// MINI INSTALL BUTTON (For headers/navbars)
// ==========================================

export const MiniInstallButton = memo(() => {
  const { isInstallable, isInstalled, promptInstall } = usePWA();

  if (isInstalled || !isInstallable) return null;

  return (
    <button
      onClick={promptInstall}
      className="flex items-center gap-2 px-3 py-1.5 bg-[#7070F5]/10 text-[#7070F5] text-sm font-medium rounded-lg hover:bg-[#7070F5]/20 transition-colors"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
      Install
    </button>
  );
});

PWAInstallPrompt.displayName = 'PWAInstallPrompt';
Benefit.displayName = 'Benefit';
IOSInstallGuide.displayName = 'IOSInstallGuide';
PWAUpdatePrompt.displayName = 'PWAUpdatePrompt';
OfflineIndicator.displayName = 'OfflineIndicator';
MiniInstallButton.displayName = 'MiniInstallButton';

export default PWAInstallPrompt;
